import React, {
    FunctionComponent,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { autorun } from 'mobx';
import { useSearchDebounce, useService, useStores } from 'Hooks';
import { UserService } from 'Services/UserService';
import FilterStore from 'Stores/FilterStore';
import { Button, Layout, Table } from 'antd';
import {
    ColumnType,
    Key,
    SorterResult,
    SortOrder,
    TablePaginationConfig,
} from 'antd/lib/table/interface';
import { images, theme } from 'variant';
import { ListSectionHeader } from 'Components/list-section-header';
import { TableFilters } from 'Components/table-filters';
import { TdWithImage } from 'Components/td-with-image';
import {
    Company as CompanyIcon,
    Location as LocationIcon,
    Users as UsersIcon,
} from 'Components/icons';
import { User } from 'Models/Users/User';
import { GetUsersRequestDto } from 'Api/Features/Users/Dtos/GetUsersRequestDto';
import { UserStatusDto } from 'Api/Features/Users/Dtos/UserStatusDto';
import { LightLocationInfo } from 'Models/Location/LightLocationInfo';
import { AdvancedFilter } from 'Models/Filters/AdvancedFilter';
import { GetUsersSortColumnDto } from 'Api/Features/Users/Dtos/GetUsersSortColumnDto';
import { SortDirectionDto } from 'Api/Features/General/Dtos/SortDirectionDto';
import { PAGE_SIZE } from 'Models/Constants';
import InviteUserModal from '../companies/id/team/invite-user';
import './index.less';
import StatusBadge from 'Components/status-badge/status-badge';

const { listSectionHeader } = images;
const { Content } = Layout;

const initialPaginationState: TablePaginationConfig = {
    current: 1,
    pageSize: PAGE_SIZE,
    showSizeChanger: true,
    position: ['bottomRight', 'topRight'],
};

const advancedFilters: AdvancedFilter[] = [
    {
        key: 'status',
        nameKey: 'Status',
        items: [
            {
                key: 'Active',
                displayNameKey: UserStatusDto.Active,
                checked: true,
            },
            {
                key: 'Deactivated',
                displayNameKey: UserStatusDto.Deactivated,
                checked: true,
            },
        ],
    },
];

const Users: FunctionComponent = () => {
    //#region Hooks
    const { t } = useTranslation();
    const { locationStore, navigationStore } = useStores();
    const history = useHistory();
    const userService = useService(UserService);
    const filterStoreRef = useRef(new FilterStore({ advancedFilters }));
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState<User[]>([]);
    const [pagination, setPagination] = useState<TablePaginationConfig>(initialPaginationState);
    const [inviteUserModalOpen, setInviteUserModalOpen] = useState(false);
    //#endregion

    //#region Filters
    const getFilterGroupOptions = (filterGroup: string, omitAll = true): string[] => {
        const filterStore = filterStoreRef.current;
        return filterStore.getGroupOptions(filterGroup, omitAll);
    };
    //#endregion

    //#region Table Content
    const getTableColumnsSortHandler = () => {
        return (usrA: User, usrB: User): number => 0;
    };

    const getTableColumnsSortDirections = (): SortOrder[] => {
        return ['ascend', 'descend', 'ascend']; // # Force to always have ascend or descend states
    };

    const getTableColumnSortOrder = (
        currentSort: SorterResult<User>,
        columnKey: GetUsersSortColumnDto
    ): SortOrder => {
        return columnKey === currentSort.columnKey ? (currentSort.order as SortOrder) : null;
    };

    const getTableColumns = (currentSort: SorterResult<User>): ColumnType<User>[] => {
        return [
            {
                key: GetUsersSortColumnDto.Name,
                title: t('User.user_name'),
                render: (user: User): ReactNode | null => {
                    return user ? (
                        <TdWithImage defaultImg={<UsersIcon />} imgSrc={user?.imageUrl}>
                            {user?.name}
                        </TdWithImage>
                    ) : null;
                },
                sorter: getTableColumnsSortHandler(),
                sortDirections: getTableColumnsSortDirections(),
                sortOrder: getTableColumnSortOrder(currentSort, GetUsersSortColumnDto.Name),
            },
            {
                key: GetUsersSortColumnDto.Email,
                title: t('User.email'),
                render: (user: User): ReactNode | null => <div>{user.email}</div>,
                sorter: getTableColumnsSortHandler(),
                sortDirections: getTableColumnsSortDirections(),
                sortOrder: getTableColumnSortOrder(currentSort, GetUsersSortColumnDto.Email),
            },
            {
                key: GetUsersSortColumnDto.CompanyName,
                title: t('Company.company'),
                render: (user: User): ReactNode | null => {
                    return user.company ? (
                        <TdWithImage defaultImg={<CompanyIcon />} imgSrc={user.company.imageUrl}>
                            {user.company?.name}
                        </TdWithImage>
                    ) : null;
                },
                sorter: getTableColumnsSortHandler(),
                sortDirections: getTableColumnsSortDirections(),
                sortOrder: getTableColumnSortOrder(currentSort, GetUsersSortColumnDto.CompanyName),
            },
            {
                key: GetUsersSortColumnDto.LocationName,
                title: t('Location.location'),
                render: (user: User): ReactNode | null => {
                    return user.company ? (
                        <TdWithImage
                            defaultImg={<LocationIcon />}
                            imgSrc={user.location?.mainImageUrl}
                        >
                            <div className="title">{user.location?.name}</div>
                        </TdWithImage>
                    ) : null;
                },
                sorter: getTableColumnsSortHandler(),
                sortDirections: getTableColumnsSortDirections(),
                sortOrder: getTableColumnSortOrder(currentSort, GetUsersSortColumnDto.LocationName),
            },
            {
                key: GetUsersSortColumnDto.Status,
                title: t('User.user_status'),
                render: (user: User): ReactNode | null => (
                    <StatusBadge status={user.statusBadgeStatus} />
                ),
                sorter: getTableColumnsSortHandler(),
                sortDirections: getTableColumnsSortDirections(),
                sortOrder: getTableColumnSortOrder(currentSort, GetUsersSortColumnDto.Status),
            },
        ];
    };

    const defaultSortOrder: SorterResult<User> = {
        columnKey: GetUsersSortColumnDto.Name as Key,
        order: 'ascend' as SortOrder,
    };

    const tableToDtoSortOrder = (
        tableSort: SorterResult<User>
    ): [GetUsersSortColumnDto, SortDirectionDto] => {
        const sortColumn: GetUsersSortColumnDto = tableSort.columnKey as GetUsersSortColumnDto;
        const sortDirection: SortDirectionDto =
            tableSort.order === 'descend'
                ? SortDirectionDto.Descending
                : SortDirectionDto.Ascending;

        return [sortColumn, sortDirection];
    };

    const [defaultColumnSort, defaultDirectionSort] = tableToDtoSortOrder(defaultSortOrder);
    const defaultTableColumns = getTableColumns(defaultSortOrder);
    const [columns, setColumns] = useState<ColumnType<User>[]>(defaultTableColumns);

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, Key[] | null>,
        sorter: SorterResult<User> | SorterResult<User>[]
    ): void => {
        if (Array.isArray(sorter)) {
            // # We support single column sort only, like the API
            // # This should not happen if columns are properly configured,
            // # but just in case, we keep the first sort only.
            sorter = sorter[0];
        }

        // Update the table sort state
        setColumns(getTableColumns(sorter));

        // Call new sorted data
        const [column, direction] = tableToDtoSortOrder(sorter);
        const filterStore = filterStoreRef.current;
        fetch({
            pagination,
            sortColumn: column,
            sortDirection: direction,
            searchTerm: filterStore.searchTerm,
            advancedFilters: filterStore.advancedFilters,
            locationIds: filterStore.currentLocationIds,
        });
    };
    //#endregion

    //#region Fetch & Effects
    useEffect(() => {
        navigationStore.clearSubmenu();
    }, [navigationStore]);

    const fetch = useCallback(
        async (params: {
            pagination: TablePaginationConfig;
            sortColumn: GetUsersSortColumnDto;
            sortDirection: SortDirectionDto;
            searchTerm: string;
            advancedFilters?: AdvancedFilter[];
            locationIds: string[];
        }) => {
            setLoading(true);
            try {
                // call api
                const request = {
                    pageSize: params.pagination.pageSize || PAGE_SIZE,
                    page: (params.pagination.current || 1) - 1,
                    sortColumn: params.sortColumn,
                    sortDirection: params.sortDirection,
                    searchTerm: params.searchTerm,
                    statuses: getFilterGroupOptions('status'),
                    hasCompany: true, // filter-out admin only users (staff)
                } as GetUsersRequestDto;

                if (params.locationIds.length > 0) {
                    request.locationIds = params.locationIds;
                }

                const [usersData, totalItems] = await userService.getUsers(request);
                setUsers(usersData);

                setPagination({
                    ...params.pagination,
                    total: totalItems,
                });
            } finally {
                setLoading(false);
            }
        },
        [userService]
    );

    const debouncedFetch = useSearchDebounce(fetch);
    useEffect(() => {
        const disposer = autorun(() => {
            const filterStore = filterStoreRef.current;
            debouncedFetch({
                pagination: initialPaginationState,
                sortColumn: defaultColumnSort,
                sortDirection: defaultDirectionSort,
                searchTerm: filterStore.searchTerm,
                advancedFilters: filterStore.advancedFilters,
                locationIds: filterStore.currentLocationIds,
            });
        });
        return (): void => {
            disposer();
        };
    }, [debouncedFetch, defaultColumnSort, defaultDirectionSort]);
    //#endregion

    //#region Actions
    const onCreationComplete = useCallback(
        (success: boolean) => {
            const filterStore = filterStoreRef.current;
            setInviteUserModalOpen(false);
            if (success) {
                setColumns(defaultTableColumns);
                fetch({
                    pagination: initialPaginationState,
                    sortColumn: defaultColumnSort,
                    sortDirection: defaultDirectionSort,
                    searchTerm: filterStore.searchTerm,
                    advancedFilters: filterStore.advancedFilters,
                    locationIds: filterStore.currentLocationIds,
                });
            }
        },
        [fetch, defaultTableColumns, defaultColumnSort, defaultDirectionSort]
    );
    //#endregion

    return (
        <div className="Users">
            <ListSectionHeader
                title={t('User.users')}
                subTitle={t('User.users_manage_all')}
                defaultImg={<UsersIcon fill={theme['primary-color']} />}
                backgroundImageUrl={listSectionHeader}
                action={
                    <Button
                        type="primary"
                        onClick={() => {
                            setInviteUserModalOpen(true);
                        }}
                    >
                        {t('User.invite_user')}
                    </Button>
                }
            />
            <Content>
                <TableFilters
                    filterStore={filterStoreRef.current}
                    includeLocations
                    availableLocations={locationStore.locations.map(
                        (location) => location as LightLocationInfo
                    )}
                    includeSearch
                    includeAdvancedFilters
                />
                <Table
                    className="table-striped-rows table-action-rows"
                    bordered
                    columns={columns}
                    rowKey={(user: User): string => user.id}
                    dataSource={users}
                    pagination={pagination}
                    loading={loading}
                    onChange={handleTableChange}
                    onRow={(user: User) => ({
                        onClick: (): void => {
                            history.push(user.consoleUrl);
                        },
                    })}
                />
            </Content>
            {inviteUserModalOpen && (
                <InviteUserModal visible={inviteUserModalOpen} onComplete={onCreationComplete} />
            )}
        </div>
    );
};

export default Users;
